import { createClient } from '@supabase/supabase-js';
import { InputConfig, TimelineEntry } from '../types';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL!;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY!;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Missing Supabase environment variables. Please create a .env file with REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY'
  );
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true
  },
  global: {
    headers: {
      'Prefer': 'return=representation'
    }
  },
  db: {
    schema: 'public'
  }
});

// Helper to get signed URL for private storage files
export async function getSignedUrl(url: string): Promise<string | null> {
  if (!url) return null;

  // If it's not a supabase storage URL, return as-is
  if (!url.includes('supabase.co/storage')) {
    return url;
  }

  try {
    const response = await fetch(`${supabaseUrl}/functions/v1/get-signed-url`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseAnonKey}`,
      },
      body: JSON.stringify({ url }),
    });

    if (!response.ok) {
      console.error('Failed to get signed URL');
      return null;
    }

    const data = await response.json();
    return data.signedUrl || null;
  } catch (error) {
    console.error('Error getting signed URL:', error);
    return null;
  }
}

// Helper functions for common queries

export const api = {
  // Onboarding Steps
  async getOnboardingSteps(options?: {
    targetAudience?: 'loan_officer' | 'admin' | 'va';
    activeOnly?: boolean;
  }) {
    let query = supabase
      .from('onboarding_steps')
      .select('*')
      .order('sequential_order', { ascending: true });

    if (options?.activeOnly !== false) {
      query = query.eq('is_active', true);
    }

    if (options?.targetAudience) {
      query = query.eq('target_audience', options.targetAudience);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data;
  },

  async getStepCategories(includeInactive = false) {
    let query = supabase
      .from('step_categories')
      .select('*')
      .order('sequence_order', { ascending: true });

    if (!includeInactive) {
      query = query.eq('is_active', true);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data;
  },

  async createStepCategory(category: {
    slug: string;
    name: string;
    description?: string | null;
    color?: string;
    accent_color?: string;
    icon?: string;
    sequence_order?: number;
  }) {
    const { data, error } = await supabase
      .from('step_categories')
      .insert({
        ...category,
        is_active: true,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateStepCategory(
    categoryId: string,
    updates: Partial<{
      name: string;
      description: string | null;
      color: string;
      accent_color: string;
      icon: string;
      is_active: boolean;
      sequence_order: number;
    }>
  ) {
    const { data, error } = await supabase
      .from('step_categories')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', categoryId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async deleteStepCategory(categoryId: string) {
    const { error } = await supabase
      .from('step_categories')
      .delete()
      .eq('id', categoryId);

    if (error) throw error;
  },

  async updateCategoryOrder(orderUpdates: { id: string; sequence_order: number }[]) {
    const mutations = orderUpdates.map(({ id, sequence_order }) =>
      supabase
        .from('step_categories')
        .update({ sequence_order, updated_at: new Date().toISOString() })
        .eq('id', id)
    );

    const results = await Promise.all(mutations);
    const errors = results.filter((r) => r.error);
    if (errors.length) throw errors[0].error;
  },

  async createOnboardingStep(step: {
    category: string;
    task_name: string;
    sequential_order: number;
    priority: string;
    target_audience: string;
    activity_type: string;
    notes?: string | null;
    required_flags?: string[] | null;
    dependencies?: string[] | null;
    is_active?: boolean;
    notify_role_on_complete?: 'admin' | 'va' | 'both' | 'none';
    input_config?: InputConfig | null;
    section?: string;
  }) {
    const { data, error } = await supabase
      .from('onboarding_steps')
      .insert({
        ...step,
        notes: step.notes ?? null,
        required_flags: step.required_flags ?? null,
        dependencies: step.dependencies ?? null,
        is_active: step.is_active ?? true,
        notify_role_on_complete: step.notify_role_on_complete ?? 'none',
        input_config: step.input_config ?? null,
        section: step.section ?? 'onboarding',
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateOnboardingStep(
    stepId: string,
    updates: Partial<{
      category: string;
      task_name: string;
      sequential_order: number;
      priority: string;
      target_audience: string;
      activity_type: string;
      notes: string | null;
      required_flags: string[] | null;
      dependencies: string[] | null;
      is_active: boolean;
      notify_role_on_complete: 'admin' | 'va' | 'both' | 'none';
      input_config: InputConfig | null;
      section: string;
    }>
  ) {
    const { data, error } = await supabase
      .from('onboarding_steps')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', stepId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async deleteOnboardingStep(stepId: string) {
    const { error } = await supabase
      .from('onboarding_steps')
      .delete()
      .eq('id', stepId);

    if (error) throw error;
  },

  // Loan Officer Progress
  async getLOProgress(loanOfficerId: string) {
    const { data, error } = await supabase
      .from('loan_officer_progress')
      .select(`
        *,
        step:onboarding_steps!inner(*)
      `)
      .eq('loan_officer_id', loanOfficerId)
      .eq('step.is_active', true)
      .order('step(sequential_order)', { ascending: true });

    if (error) throw error;
    return data;
  },

  async updateProgressStatus(
    progressId: string,
    status: string,
    notes?: string,
    blockedReason?: string
  ) {
    const updates: any = {
      status,
      updated_at: new Date().toISOString()
    };

    if (status === 'in_progress' && !notes) {
      updates.started_date = new Date().toISOString();
    }

    if (status === 'completed') {
      updates.completed_date = new Date().toISOString();
    }

    if (notes) {
      updates.notes = notes;
    }

    if (blockedReason) {
      updates.blocked_reason = blockedReason;
    }

    const { data, error } = await supabase
      .from('loan_officer_progress')
      .update(updates)
      .eq('id', progressId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Custom Tasks
  async getCustomTasks(loanOfficerId: string) {
    const { data, error } = await supabase
      .from('custom_tasks')
      .select('*')
      .eq('loan_officer_id', loanOfficerId)
      .order('due_date', { ascending: true, nullsFirst: false });

    if (error) throw error;
    return data;
  },

  async createCustomTask(task: {
    loan_officer_id: string;
    title: string;
    description?: string;
    priority?: string;
    due_date?: string;
    assigned_by: string;
  }) {
    const { data, error } = await supabase
      .from('custom_tasks')
      .insert(task)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateCustomTask(taskId: string, updates: any) {
    const { data, error } = await supabase
      .from('custom_tasks')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', taskId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Progress Summary
  async getProgressSummary(loanOfficerId: string) {
    const { data, error } = await supabase
      .from('lo_progress_summary')
      .select('*')
      .eq('loan_officer_id', loanOfficerId)
      .maybeSingle(); // Use maybeSingle() instead of single() to handle 0 or 1 rows

    if (error) throw error;
    return data;
  },

  // Activity Log
  async getActivityLog(loanOfficerId?: string, limit = 50) {
    let query = supabase
      .from('activity_log')
      .select(`
        *,
        step:onboarding_steps(*),
        task:custom_tasks(*)
      `)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (loanOfficerId) {
      query = query.eq('loan_officer_id', loanOfficerId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data;
  },

  // Admin: Get all LOs with progress
  async getAllLOsWithProgress() {
    const { data, error } = await supabase
      .from('lo_progress_summary')
      .select('*')
      .order('completion_percentage', { ascending: false });

    if (error) throw error;
    return data;
  },

  // Admin: Get category progress for all LOs
  async getAllCategoryProgress() {
    const { data, error } = await supabase
      .from('lo_category_progress')
      .select('*')
      .order('sequence_order', { ascending: true });

    if (error) throw error;
    return data;
  },

  // Admin: Initialize onboarding for new LO
  async initializeLOOnboarding(loanOfficerId: string) {
    // First, get all active steps for loan officers
    const { data: steps, error: stepsError } = await supabase
      .from('onboarding_steps')
      .select('id')
      .eq('is_active', true)
      .eq('target_audience', 'loan_officer');

    if (stepsError) throw stepsError;

    // Create progress records for each step
    const progressRecords = steps.map(step => ({
      loan_officer_id: loanOfficerId,
      step_id: step.id,
      status: 'not_started',
      assigned_date: new Date().toISOString()
    }));

    const { data, error } = await supabase
      .from('loan_officer_progress')
      .insert(progressRecords)
      .select();

    if (error) throw error;
    return data;
  },

  // Admin: Mark step complete for an LO
  async adminCompleteStep(progressId: string, adminId: string, notes?: string) {
    const { data, error } = await supabase
      .from('loan_officer_progress')
      .update({
        status: 'completed',
        completed_date: new Date().toISOString(),
        completed_by: adminId,
        notes: notes || null,
        updated_at: new Date().toISOString()
      })
      .eq('id', progressId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Admin: Assign specific step to an LO
  async assignStepToLO(loanOfficerId: string, stepId: string) {
    // Check if already assigned
    const { data: existing } = await supabase
      .from('loan_officer_progress')
      .select('id')
      .eq('loan_officer_id', loanOfficerId)
      .eq('step_id', stepId)
      .maybeSingle();

    if (existing) {
      throw new Error('This step is already assigned to this loan officer');
    }

    const { data, error } = await supabase
      .from('loan_officer_progress')
      .insert({
        loan_officer_id: loanOfficerId,
        step_id: stepId,
        status: 'not_started',
        assigned_date: new Date().toISOString()
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Admin: Remove step from LO
  async removeStepFromLO(progressId: string) {
    const { error } = await supabase
      .from('loan_officer_progress')
      .delete()
      .eq('id', progressId);

    if (error) throw error;
  },

  // Workflow Email Configuration
  async getWorkflowEmailConfigs() {
    const { data, error } = await supabase
      .from('workflow_email_configs')
      .select('*')
      .order('workflow_type', { ascending: true });

    if (error) throw error;
    return data;
  },

  async getWorkflowEmailConfig(workflowType: string) {
    const { data, error } = await supabase
      .from('workflow_email_configs')
      .select('*')
      .eq('workflow_type', workflowType)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  async updateWorkflowEmailConfig(
    workflowType: string,
    recipientEmail: string,
    recipientName?: string,
    notes?: string,
    updatedBy?: string
  ) {
    // Use upsert to insert if not exists, or update if exists
    const { data, error } = await supabase
      .from('workflow_email_configs')
      .upsert({
        workflow_type: workflowType,
        recipient_email: recipientEmail,
        recipient_name: recipientName || null,
        notes: notes || null,
        updated_by: updatedBy || null,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'workflow_type' // Update on conflict with workflow_type
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async createWorkflowEmailConfig(params: {
    workflow_type: string;
    recipient_email: string;
    recipient_name?: string;
    notes?: string;
    updated_by?: string;
  }) {
    const { data, error} = await supabase
      .from('workflow_email_configs')
      .insert({
        workflow_type: params.workflow_type,
        recipient_email: params.recipient_email,
        recipient_name: params.recipient_name || null,
        notes: params.notes || null,
        updated_by: params.updated_by || null
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // A2P Phone Registration Tracking
  async getA2PRegistration(loanOfficerId: string) {
    const { data, error } = await supabase
      .from('a2p_phone_registrations')
      .select('*')
      .eq('loan_officer_id', loanOfficerId)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  async getAllA2PRegistrations() {
    const { data, error } = await supabase
      .from('a2p_phone_registration_summary')
      .select('*')
      .order('updated_at', { ascending: false });

    if (error) throw error;
    return data;
  },

  async getA2PStatusHistory(registrationId: string) {
    const { data, error } = await supabase
      .from('a2p_phone_status_history')
      .select('*')
      .eq('a2p_phone_registration_id', registrationId)
      .order('changed_at', { ascending: false });

    if (error) throw error;
    return data;
  },

  async updateA2PRegistration(registrationId: string, updates: {
    brand_status?: string;
    brand_reference_id?: string;
    brand_notes?: string;
    brand_failure_reason?: string;
    campaign_status?: string;
    campaign_reference_id?: string;
    campaign_notes?: string;
    campaign_failure_reason?: string;
    phone_number?: string;
    updated_by?: string;
  }) {
    const updateData: any = {
      ...updates,
      updated_at: new Date().toISOString()
    };

    // Set timestamp fields based on status
    if (updates.brand_status === 'pending') {
      updateData.brand_submitted_at = new Date().toISOString();
    } else if (updates.brand_status === 'approved') {
      updateData.brand_approved_at = new Date().toISOString();
    } else if (updates.brand_status === 'failed') {
      updateData.brand_failed_at = new Date().toISOString();
    }

    if (updates.campaign_status === 'pending') {
      updateData.campaign_submitted_at = new Date().toISOString();
    } else if (updates.campaign_status === 'approved') {
      updateData.campaign_approved_at = new Date().toISOString();
    } else if (updates.campaign_status === 'failed') {
      updateData.campaign_failed_at = new Date().toISOString();
    }

    const { data, error } = await supabase
      .from('a2p_phone_registrations')
      .update(updateData)
      .eq('id', registrationId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async createA2PRegistration(params: {
    loan_officer_id: string;
    phone_number?: string;
    brand_status?: string;
    campaign_status?: string;
  }) {
    const { data, error } = await supabase
      .from('a2p_phone_registrations')
      .insert({
        loan_officer_id: params.loan_officer_id,
        phone_number: params.phone_number || null,
        brand_status: params.brand_status || 'not_started',
        campaign_status: params.campaign_status || 'not_started'
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // A2P Business Registration (for domain/email setup)
  async getA2PBusinessRegistration(loanOfficerId: string) {
    const { data, error } = await supabase
      .from('a2p_registrations')
      .select('*')
      .eq('loan_officer_id', loanOfficerId)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  async getAllA2PBusinessRegistrations() {
    const { data, error } = await supabase
      .from('a2p_registrations')
      .select('loan_officer_id, business_name');

    if (error) throw error;
    return data;
  },

  async createA2PBusinessRegistration(params: {
    loan_officer_id: string;
    business_name: string;
    street_address?: string;
    city?: string;
    state?: string;
    zip_code?: string;
    ein?: string;
    irs_paperwork?: string;
  }) {
    const { data, error } = await supabase
      .from('a2p_registrations')
      .insert({
        loan_officer_id: params.loan_officer_id,
        business_name: params.business_name,
        street_address: params.street_address || null,
        city: params.city || null,
        state: params.state || null,
        zip_code: params.zip_code || null,
        ein: params.ein || null,
        irs_paperwork: params.irs_paperwork || null,
        brand_approved: false,
        campaign_approved: false,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateA2PBusinessRegistration(id: string, updates: {
    business_name?: string;
    street_address?: string;
    city?: string;
    state?: string;
    zip_code?: string;
    ein?: string;
    irs_paperwork?: string;
    brand_approved?: boolean;
    campaign_approved?: boolean;
  }) {
    const { data, error } = await supabase
      .from('a2p_registrations')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // State Sponsorships
  async getSponsorships(loanOfficerId: string) {
    const { data, error } = await supabase
      .from('state_sponsorships')
      .select('*')
      .eq('loan_officer_id', loanOfficerId)
      .order('state_name', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  // Bulk update step section (move between onboarding/hands_on)
  async bulkUpdateStepSection(stepIds: string[], section: string) {
    const promises = stepIds.map((id) =>
      supabase
        .from('onboarding_steps')
        .update({
          section,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
    );

    const results = await Promise.all(promises);
    const errors = results.filter(r => r.error);

    if (errors.length > 0) {
      throw errors[0].error;
    }

    return true;
  },

  // Bulk update step sequential order
  async bulkUpdateStepOrder(stepOrders: { id: string; sequential_order: number }[]) {
    const promises = stepOrders.map(({ id, sequential_order }) =>
      supabase
        .from('onboarding_steps')
        .update({
          sequential_order,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
    );

    const results = await Promise.all(promises);
    const errors = results.filter(r => r.error);

    if (errors.length > 0) {
      throw errors[0].error;
    }

    return true;
  },

  // Signature Templates
  async getDefaultSignatureTemplate() {
    const { data, error } = await supabase
      .from('signature_templates')
      .select('*')
      .eq('is_default', true)
      .eq('is_active', true)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  async saveSignatureTemplate(params: {
    template_name: string;
    description?: string;
    template_config: any;
    created_by?: string;
    is_default?: boolean;
  }) {
    // If making this the default, unset other defaults first
    if (params.is_default) {
      await supabase
        .from('signature_templates')
        .update({ is_default: false })
        .eq('is_default', true);
    }

    const { data, error } = await supabase
      .from('signature_templates')
      .insert({
        template_name: params.template_name,
        description: params.description || null,
        template_config: params.template_config,
        created_by: params.created_by || null,
        is_default: params.is_default || false,
        is_active: true
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateSignatureTemplate(templateId: string, params: {
    template_config: any;
    is_default?: boolean;
  }) {
    // If making this the default, unset other defaults first
    if (params.is_default) {
      await supabase
        .from('signature_templates')
        .update({ is_default: false })
        .eq('is_default', true)
        .neq('id', templateId);
    }

    const { data, error } = await supabase
      .from('signature_templates')
      .update({
        template_config: params.template_config,
        is_default: params.is_default,
        updated_at: new Date().toISOString()
      })
      .eq('id', templateId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // ============================================
  // PROSPECT SYSTEM API
  // ============================================

  // Create a new recruit (pre-prospect stage) and send expectations email
  async createRecruit(params: {
    first_name: string;
    last_name: string;
    phone: string;
    email: string;
    title: 'Sr Mortgage Broker' | 'Executive Branch Manager';
    nmls: string;
    expected_start_date: string;
    recruiter_id: string;
    notes?: string;
  }) {
    const { data, error } = await supabase.functions.invoke('create-recruit', {
      body: params
    });

    if (error) throw error;
    if (!data.success) throw new Error(data.error);
    return data;
  },

  // Promote a recruit to prospect status
  async promoteRecruit(params: {
    prospect_id: string;
    admin_id: string;
  }) {
    const { data, error } = await supabase.functions.invoke('promote-recruit', {
      body: params
    });

    if (error) throw error;
    if (!data.success) throw new Error(data.error);
    return data;
  },

  // Create a new prospect (calls edge function)
  async createProspect(params: {
    first_name: string;
    last_name: string;
    preferred_first_name?: string;
    title: 'Sr Mortgage Broker' | 'Executive Branch Manager';
    phone?: string;
    email?: string;
    nmls?: string;
    expected_month?: string;
    expected_start_date?: string;
    state_licenses?: string[];
    place_of_work?: 'Remote' | 'In Office';
    notes?: string;
    created_by: string;
  }) {
    const { data, error } = await supabase.functions.invoke('create-prospect', {
      body: params
    });

    if (error) throw error;
    if (!data.success) throw new Error(data.error);
    return data;
  },

  // Get all prospects with optional filters
  async getProspects(filters?: {
    status?: string;
    created_by?: string;
    search?: string;
  }) {
    let query = supabase
      .from('prospects')
      .select('*')
      .order('created_at', { ascending: false });

    if (filters?.status) {
      query = query.eq('status', filters.status);
    }

    if (filters?.created_by) {
      query = query.eq('created_by', filters.created_by);
    }

    if (filters?.search) {
      query = query.or(
        `first_name.ilike.%${filters.search}%,last_name.ilike.%${filters.search}%,email.ilike.%${filters.search}%,company_email.ilike.%${filters.search}%`
      );
    }

    const { data, error } = await query;
    if (error) throw error;
    return data;
  },

  // Get a single prospect by ID
  async getProspect(prospectId: string) {
    const { data, error } = await supabase
      .from('prospects')
      .select('*')
      .eq('id', prospectId)
      .single();

    if (error) throw error;
    return data;
  },

  // Start onboarding for a prospect (calls edge function)
  async startProspectOnboarding(params: {
    prospect_id: string;
    admin_id: string;
  }) {
    const { data, error } = await supabase.functions.invoke('start-prospect-onboarding', {
      body: params
    });

    if (error) {
      // Check if error has a message from the function
      const errorMsg = error.message || 'Failed to start onboarding';
      throw new Error(errorMsg);
    }
    if (data && !data.success) {
      throw new Error(data.error || 'Failed to start onboarding');
    }
    return data;
  },

  // Update prospect email after VA creates it in GoDaddy (calls edge function)
  async updateProspectEmail(params: {
    prospect_id: string;
    company_email: string;
    admin_id: string;
  }) {
    const { data, error} = await supabase.functions.invoke('update-prospect-email', {
      body: params
    });

    if (error) throw error;
    if (!data.success) throw new Error(data.error);
    return data;
  },

  // Update prospect details (direct database update)
  async updateProspect(params: {
    prospect_id: string;
    first_name: string;
    last_name: string;
    preferred_first_name: string | null;
    title: string;
    phone: string | null;
    email: string | null;
    nmls: string | null;
    place_of_work?: string | null;
    expected_month: string | null;
    expected_start_date: string | null;
    state_licenses?: string[] | null;
    notes?: string | null;
  }) {
    const { error } = await supabase
      .from('prospects')
      .update({
        first_name: params.first_name,
        last_name: params.last_name,
        preferred_first_name: params.preferred_first_name,
        title: params.title,
        phone: params.phone,
        email: params.email,
        nmls: params.nmls,
        place_of_work: params.place_of_work ?? null,
        expected_month: params.expected_month,
        expected_start_date: params.expected_start_date,
        state_licenses: params.state_licenses ?? null,
        notes: params.notes ?? null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', params.prospect_id);

    if (error) throw error;
    return { success: true };
  },

  // Delete a prospect (hard delete)
  // Optionally also delete the linked LO profile if alsoDeleteProfileId is provided
  async deleteProspect(prospectId: string, alsoDeleteProfileId?: string) {
    // Cancel any scheduled emails for this prospect
    await supabase
      .from('delayed_edge_function_calls')
      .update({ status: 'cancelled' })
      .eq('prospect_id', prospectId)
      .eq('status', 'pending');

    // Delete any associated prospect_onboarding_data
    await supabase
      .from('prospect_onboarding_data')
      .delete()
      .eq('prospect_id', prospectId);

    // Delete associated custom tasks (GoDaddy email creation, etc.)
    // Tasks store prospect ID in notes field
    await supabase
      .from('custom_tasks')
      .delete()
      .ilike('notes', `%${prospectId}%`);

    // Then delete the prospect
    const { error } = await supabase
      .from('prospects')
      .delete()
      .eq('id', prospectId);

    if (error) throw error;

    // If requested, also delete the linked Loan Officer profile
    if (alsoDeleteProfileId) {
      // Delete related records first (order matters due to FK constraints)
      // These have ON DELETE SET NULL or CASCADE, but we clean up explicitly

      // Delete loan_officer_progress records
      await supabase
        .from('loan_officer_progress')
        .delete()
        .eq('loan_officer_id', alsoDeleteProfileId);

      // Delete custom_tasks assigned to this LO
      await supabase
        .from('custom_tasks')
        .delete()
        .eq('loan_officer_id', alsoDeleteProfileId);

      // Delete activity_log entries for this LO
      await supabase
        .from('activity_log')
        .delete()
        .eq('loan_officer_id', alsoDeleteProfileId);

      // Delete w9_submissions
      await supabase
        .from('w9_submissions')
        .delete()
        .eq('loan_officer_id', alsoDeleteProfileId);

      // Delete banking_information
      await supabase
        .from('banking_information')
        .delete()
        .eq('loan_officer_id', alsoDeleteProfileId);

      // Delete notifications
      await supabase
        .from('notifications')
        .delete()
        .eq('loan_officer_id', alsoDeleteProfileId);

      // Delete lo_comments
      await supabase
        .from('lo_comments')
        .delete()
        .eq('loan_officer_id', alsoDeleteProfileId);

      // Delete handoffs
      await supabase
        .from('handoffs')
        .delete()
        .eq('loan_officer_id', alsoDeleteProfileId);

      // Delete a2p_phone_registrations
      await supabase
        .from('a2p_phone_registrations')
        .delete()
        .eq('loan_officer_id', alsoDeleteProfileId);

      // Delete a2p_registrations
      await supabase
        .from('a2p_registrations')
        .delete()
        .eq('loan_officer_id', alsoDeleteProfileId);

      // Delete parking_pass_submissions
      await supabase
        .from('parking_pass_submissions')
        .delete()
        .eq('loan_officer_id', alsoDeleteProfileId);

      // Delete delayed_edge_function_calls
      await supabase
        .from('delayed_edge_function_calls')
        .delete()
        .eq('loan_officer_id', alsoDeleteProfileId);

      // Finally delete the profile itself
      const { error: profileError } = await supabase
        .from('profiles')
        .delete()
        .eq('id', alsoDeleteProfileId);

      if (profileError) throw profileError;
    }

    return { success: true };
  },

  // Send onboarding email to prospect (calls edge function)
  async sendProspectOnboardingEmail(params: {
    prospect_id: string;
  }) {
    const { data, error } = await supabase.functions.invoke('send-prospect-onboarding-email', {
      body: params
    });

    if (error) throw error;
    if (!data.success) throw new Error(data.error);
    return data;
  },

  // Resend the recruit welcome ("What to Expect") email to a prospect's personal email
  async sendRecruitWelcomeEmail(params: {
    prospect_id: string;
  }) {
    const { data, error } = await supabase.functions.invoke('send-recruit-welcome-email', {
      body: params
    });

    if (error) throw error;
    if (!data.success) throw new Error(data.error);
    return data;
  },

  // Get prospect onboarding data by prospect ID
  async getProspectOnboardingData(prospectId: string) {
    const { data, error } = await supabase
      .from('prospect_onboarding_data')
      .select('*')
      .eq('prospect_id', prospectId)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  // Validate recruiter token and return recruiter info
  async validateRecruiterToken(token: string) {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, first_name, last_name, email')
      .eq('recruiter_token', token)
      .maybeSingle();

    if (error) throw error;
    if (!data) throw new Error('Invalid recruiter token');
    return data;
  },

  // Create prospect from recruiter form (public, token-validated)
  async createProspectFromRecruiter(params: {
    recruiter_token: string;
    first_name: string;
    last_name: string;
    title: 'Sr Mortgage Broker' | 'Executive Branch Manager';
    phone?: string;
    email?: string;
    nmls?: string;
    expected_month?: string;
    expected_start_date?: string;
    state_licenses?: string[];
  }) {
    // First validate the recruiter token
    const recruiter = await this.validateRecruiterToken(params.recruiter_token);

    // Create the prospect with created_by set to recruiter's ID
    const { data, error } = await supabase
      .from('prospects')
      .insert({
        first_name: params.first_name,
        last_name: params.last_name,
        title: params.title,
        phone: params.phone || null,
        email: params.email || null,
        nmls: params.nmls || null,
        expected_month: params.expected_month || null,
        expected_start_date: params.expected_start_date || null,
        state_licenses: params.state_licenses || [],
        created_by: recruiter.id,
        status: 'pending',
      })
      .select()
      .single();

    if (error) throw error;
    return { success: true, prospect: data, recruiter };
  },

  // Get upcoming prospects for VA dashboard (within 7 days)
  async getUpcomingProspects() {
    const { data, error } = await supabase
      .from('upcoming_prospects_for_va')
      .select('*');

    if (error) throw error;
    return data;
  },

  // Get pending scheduled emails for all prospects or a specific one
  async getScheduledEmails(prospectId?: string) {
    let query = supabase
      .from('delayed_edge_function_calls')
      .select('*')
      .eq('status', 'pending')
      .eq('function_name', 'send-prospect-onboarding-email')
      .order('execute_after', { ascending: true });

    if (prospectId) {
      query = query.eq('prospect_id', prospectId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  // Cancel a scheduled email (deletes the pending record)
  async cancelScheduledEmail(callId: string) {
    const { error } = await supabase
      .from('delayed_edge_function_calls')
      .delete()
      .eq('id', callId)
      .eq('status', 'pending');

    if (error) throw error;
    return { success: true };
  },

  async getW9Submission(loanOfficerId: string) {
    const { data, error } = await supabase
      .from('w9_submissions')
      .select('*')
      .eq('loan_officer_id', loanOfficerId)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  // Submit W9 form
  async submitW9(params: {
    loan_officer_id: string;
    name: string;
    business_name?: string;
    tax_classification: string;
    llc_tax_classification?: string;
    other_classification?: string;
    exempt_payee_code?: string;
    exemption_from_fatca_code?: string;
    address: string;
    city: string;
    state: string;
    zip_code: string;
    ssn_or_ein: string;
    pdf_url: string;
  }) {
    // Check if W9 already exists
    const existing = await this.getW9Submission(params.loan_officer_id);

    if (existing) {
      // Update existing
      const { data, error } = await supabase
        .from('w9_submissions')
        .update({
          ...params,
          submitted_at: new Date().toISOString(),
        })
        .eq('id', existing.id)
        .select()
        .single();

      if (error) throw error;

      // Mark profile as W9 completed
      await supabase
        .from('profiles')
        .update({ w9_completed: true })
        .eq('id', params.loan_officer_id);

      return data;
    } else {
      // Insert new
      const { data, error } = await supabase
        .from('w9_submissions')
        .insert({
          ...params,
          submitted_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (error) throw error;

      // Mark profile as W9 completed
      await supabase
        .from('profiles')
        .update({ w9_completed: true })
        .eq('id', params.loan_officer_id);

      return data;
    }
  },

  // Get banking information for a loan officer
  async getBankingInformation(loanOfficerId: string) {
    const { data, error } = await supabase
      .from('banking_information')
      .select('*')
      .eq('loan_officer_id', loanOfficerId)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  // Submit banking information
  async submitBankingInformation(params: {
    loan_officer_id: string;
    account_holder_name: string;
    bank_name: string;
    routing_number: string;
    account_number: string;
    account_type: 'checking' | 'savings';
  }) {
    // Check if banking info already exists
    const existing = await this.getBankingInformation(params.loan_officer_id);

    if (existing) {
      // Update existing
      const { data, error } = await supabase
        .from('banking_information')
        .update({
          ...params,
          submitted_at: new Date().toISOString(),
        })
        .eq('id', existing.id)
        .select()
        .single();

      if (error) throw error;

      // Mark profile as banking completed
      await supabase
        .from('profiles')
        .update({ banking_completed: true })
        .eq('id', params.loan_officer_id);

      return data;
    } else {
      // Insert new
      const { data, error } = await supabase
        .from('banking_information')
        .insert({
          ...params,
          submitted_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (error) throw error;

      // Mark profile as banking completed
      await supabase
        .from('profiles')
        .update({ banking_completed: true })
        .eq('id', params.loan_officer_id);

      return data;
    }
  },

  // Update profile phone number (for CRM phone task)
  async updateLOPhone(loanOfficerId: string, phone: string) {
    const { data, error } = await supabase
      .from('profiles')
      .update({ phone })
      .eq('id', loanOfficerId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Get parking pass submission for a loan officer
  async getParkingPassSubmission(loanOfficerId: string) {
    const { data, error} = await supabase
      .from('parking_pass_submissions')
      .select('*')
      .eq('loan_officer_id', loanOfficerId)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  // Loan Officer CRUD Operations

  // Create a new profile (loan officer)
  async createLoanOfficer(params: {
    first_name: string;
    last_name: string;
    email: string;
    personal_email: string | null;
    phone: string | null;
    cell_number: string | null;
    nmls: string | null;
    dre_number: string | null;
    title: string | null;
    office_address: string | null;
    timezone: string;
    user_role: string;
    profile_flags?: Record<string, boolean>;
  }) {
    // NMLS stored as string in profiles table
    const { data, error } = await supabase
      .from('profiles')
      .insert({
        first_name: params.first_name,
        last_name: params.last_name,
        full_name: `${params.first_name} ${params.last_name}`,
        email: params.email,
        personal_email: params.personal_email,
        phone: params.phone,
        cell_number: params.cell_number,
        nmls_number: params.nmls,
        dre_number: params.dre_number,
        title: params.title,
        office_address: params.office_address,
        timezone: params.timezone,
        user_role: params.user_role,
        profile_flags: params.profile_flags || {},
        employment_status: true,
        created_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Update an existing profile (loan officer)
  async updateLoanOfficer(loanOfficerId: string, updates: {
    first_name?: string;
    last_name?: string;
    email?: string;
    personal_email?: string | null;
    phone?: string | null;
    cell_number?: string | null;
    nmls?: string | null;
    dre_number?: string | null;
    title?: string | null;
    office_address?: string | null;
    timezone?: string;
    user_role?: string;
    headshot?: string | null;
    email_signature_url?: string | null;
  }) {
    // If first_name or last_name is updated, update full_name too
    const updateData: any = { ...updates };

    if (updates.first_name || updates.last_name) {
      // Need to fetch current data to build full_name
      const { data: currentLO } = await supabase
        .from('profiles')
        .select('first_name, last_name')
        .eq('id', loanOfficerId)
        .single();

      if (currentLO) {
        const firstName = updates.first_name || currentLO.first_name;
        const lastName = updates.last_name || currentLO.last_name;
        updateData.full_name = `${firstName} ${lastName}`;
      }
    }

    const { data, error } = await supabase
      .from('profiles')
      .update(updateData)
      .eq('id', loanOfficerId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Delete (soft delete) a profile
  async deleteLoanOfficer(loanOfficerId: string) {
    // Instead of hard delete, we'll set employment_status to false
    const { data, error } = await supabase
      .from('profiles')
      .update({ employment_status: false })
      .eq('id', loanOfficerId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Upload headshot to Supabase Storage
  // Path structure: {userId}/headshots/{filename} - matches RLS policy
  async uploadLOHeadshot(loanOfficerId: string, file: File): Promise<string> {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}.${fileExt}`;
    // Path must start with userId to match RLS policy
    const filePath = `${loanOfficerId}/headshots/${fileName}`;

    // Upload file to Supabase Storage (using onboarding-documents bucket)
    const { error: uploadError } = await supabase.storage
      .from('onboarding-documents')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true,
      });

    if (uploadError) throw uploadError;

    // Store the path (will use signed URL for access when bucket is private)
    // Update loan officer record with storage path
    await this.updateLoanOfficer(loanOfficerId, { headshot: filePath });

    return filePath;
  },

  // Upload onboarding document (Banking forms, etc. - NOT W9)
  // Path structure: {userId}/{docType}/{filename} - matches RLS policy
  // Banking docs go to sensitive-documents bucket, others to onboarding-documents
  async uploadOnboardingDocument(loanOfficerId: string, file: File, filePath: string): Promise<string> {
    // Route sensitive files (banking) to private bucket
    const isSensitive = filePath.includes('/banking/');
    const bucket = isSensitive ? 'sensitive-documents' : 'onboarding-documents';

    const { error: uploadError } = await supabase.storage
      .from(bucket)
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true,
      });

    if (uploadError) throw uploadError;

    // Return the storage path (not public URL)
    return filePath;
  },

  // Upload W9 to private w9-forms bucket
  // Path structure: {userId}/w9-{timestamp}.pdf - matches RLS policy
  async uploadW9Document(loanOfficerId: string, file: File): Promise<string> {
    const filePath = `${loanOfficerId}/w9-${Date.now()}.pdf`;

    const { error: uploadError } = await supabase.storage
      .from('w9-forms')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true,
      });

    if (uploadError) throw uploadError;

    // Return the storage path (not URL - will use signed URL for access)
    return filePath;
  },

  // Get a signed URL for a storage file (for private bucket access)
  // Uses the edge function which has service role access
  async getSignedUrl(bucket: string, path: string, expiresIn: number = 3600): Promise<string | null> {
    // Handle full URLs - extract bucket and path from URL
    let actualBucket = bucket;
    let storagePath = path;

    if (path.startsWith('http')) {
      // Extract bucket and path from full URL
      // Format: https://xxx.supabase.co/storage/v1/object/public/bucket-name/path/to/file
      const match = path.match(/\/storage\/v1\/object\/public\/([^/]+)\/(.+)$/);
      if (match) {
        actualBucket = match[1];
        storagePath = match[2];
      }
    }

    try {
      const response = await fetch(`${supabaseUrl}/functions/v1/get-signed-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify({ bucket: actualBucket, path: storagePath }),
      });

      if (!response.ok) {
        console.error('Failed to get signed URL:', response.status);
        return null;
      }

      const data = await response.json();
      return data.signedUrl || null;
    } catch (error) {
      console.error('Error getting signed URL:', error);
      return null;
    }
  },

  // Extract storage path from a full URL or return path as-is
  extractStoragePath(urlOrPath: string, bucket: string): string {
    // If it's already a path (no http), return as-is
    if (!urlOrPath.startsWith('http')) {
      return urlOrPath;
    }
    // Extract path from full URL
    // Format: https://xxx.supabase.co/storage/v1/object/public/bucket/path
    const regex = new RegExp(`/storage/v1/object/public/${bucket}/(.+)`);
    const match = urlOrPath.match(regex);
    return match ? match[1] : urlOrPath;
  },

  // Bulk create profiles (loan officers)
  async bulkCreateLoanOfficers(losData: Array<{
    first_name: string;
    last_name: string;
    email: string;
    personal_email?: string | null;
    phone?: string | null;
    cell_number?: string | null;
    nmls?: string | null;
    dre_number?: string | null;
    title?: string | null;
    office_address?: string | null;
    timezone?: string;
    user_role?: string;
  }>) {
    const records = losData.map(lo => ({
      first_name: lo.first_name,
      last_name: lo.last_name,
      full_name: `${lo.first_name} ${lo.last_name}`,
      email: lo.email,
      personal_email: lo.personal_email || null,
      phone: lo.phone || null,
      cell_number: lo.cell_number || null,
      nmls_number: lo.nmls || null,
      dre_number: lo.dre_number || null,
      title: lo.title || null,
      office_address: lo.office_address || null,
      timezone: lo.timezone || 'America/Los_Angeles',
      user_role: lo.user_role || 'user',
      employment_status: true,
      created_at: new Date().toISOString(),
    }));

    const { data, error } = await supabase
      .from('profiles')
      .insert(records)
      .select();

    if (error) throw error;

    // Initialize onboarding for each new LO
    if (data && data.length > 0) {
      await Promise.all(
        data.map(lo => this.initializeLOOnboarding(lo.id))
      );
    }

    return {
      success: true,
      created: data?.length || 0,
      loanOfficers: data,
    };
  },

  // ============================================
  // NOTIFICATIONS & COMMENTS SYSTEM
  // ============================================

  // Get notifications for the current user
  async getNotifications(options?: {
    limit?: number;
    unreadOnly?: boolean;
  }) {
    let query = supabase
      .from('notifications')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(options?.limit || 50);

    if (options?.unreadOnly) {
      query = query.eq('is_read', false);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data;
  },

  // Get unread notification count
  async getUnreadNotificationCount() {
    const { count, error } = await supabase
      .from('notifications')
      .select('*', { count: 'exact', head: true })
      .eq('is_read', false);

    if (error) throw error;
    return count || 0;
  },

  // Mark a notification as read
  async markNotificationRead(notificationId: string) {
    const { data, error } = await supabase
      .from('notifications')
      .update({
        is_read: true,
        read_at: new Date().toISOString()
      })
      .eq('id', notificationId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Mark all notifications as read
  async markAllNotificationsRead() {
    const { error } = await supabase
      .from('notifications')
      .update({
        is_read: true,
        read_at: new Date().toISOString()
      })
      .eq('is_read', false);

    if (error) throw error;
    return { success: true };
  },

  // Create a notification
  async createNotification(params: {
    recipient_id: string;
    recipient_role: 'admin' | 'va';
    type: string;
    title: string;
    message?: string;
    loan_officer_id?: string;
    prospect_id?: string;
    step_id?: string;
    progress_id?: string;
    task_id?: string;
    sender_id?: string;
    sender_name?: string;
    rule_id?: string;
    metadata?: Record<string, any>;
  }) {
    const { data, error } = await supabase
      .from('notifications')
      .insert({
        ...params,
        is_read: false,
        is_email_sent: false,
        created_at: new Date().toISOString()
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Get attention items (pending handoffs, blocked steps) for dashboard
  async getAttentionItems(userRole: 'admin' | 'va') {
    // Get pending handoffs directed to this role
    const { data: handoffs, error: handoffsError } = await supabase
      .from('handoffs')
      .select(`
        *,
        from_user:profiles!handoffs_from_user_id_fkey(first_name, last_name),
        loan_officer:profiles!handoffs_loan_officer_id_fkey(id, first_name, last_name, email)
      `)
      .eq('to_role', userRole)
      .eq('is_acknowledged', false)
      .order('created_at', { ascending: false })
      .limit(10);

    if (handoffsError) throw handoffsError;

    // Get blocked steps (all blocked items, staff should see these)
    const { data: blockedSteps, error: blockedError } = await supabase
      .from('loan_officer_progress')
      .select(`
        *,
        step:onboarding_steps(*),
        loan_officer:profiles!loan_officer_progress_loan_officer_id_fkey(id, first_name, last_name, email)
      `)
      .eq('status', 'blocked')
      .order('updated_at', { ascending: false })
      .limit(10);

    if (blockedError) throw blockedError;

    return {
      handoffs: handoffs || [],
      blockedSteps: blockedSteps || [],
      totalCount: (handoffs?.length || 0) + (blockedSteps?.length || 0)
    };
  },

  // ============================================
  // LO COMMENTS (Internal Staff Notes)
  // ============================================

  // Get comments for a loan officer
  async getLOComments(loanOfficerId: string) {
    const { data, error } = await supabase
      .from('lo_comments')
      .select('*')
      .eq('loan_officer_id', loanOfficerId)
      .eq('is_deleted', false)
      .order('created_at', { ascending: true });

    if (error) throw error;

    // Organize into threads (top-level comments with replies)
    const topLevelComments = data?.filter(c => !c.parent_id) || [];
    const replies = data?.filter(c => c.parent_id) || [];

    return topLevelComments.map(comment => ({
      ...comment,
      replies: replies.filter(r => r.parent_id === comment.id)
    }));
  },

  // Create a comment on a loan officer
  async createLOComment(params: {
    loan_officer_id: string;
    author_id: string;
    author_name: string;
    author_role: 'admin' | 'va';
    content: string;
    parent_id?: string;
    mentions?: string[];
    attachments?: Array<{ name: string; url: string; type: string }>;
  }) {
    const { data, error } = await supabase
      .from('lo_comments')
      .insert({
        loan_officer_id: params.loan_officer_id,
        author_id: params.author_id,
        author_name: params.author_name,
        author_role: params.author_role,
        content: params.content,
        parent_id: params.parent_id || null,
        mentions: params.mentions || [],
        attachments: params.attachments || [],
        is_edited: false,
        is_deleted: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Update a comment
  async updateLOComment(commentId: string, content: string) {
    const { data, error } = await supabase
      .from('lo_comments')
      .update({
        content,
        is_edited: true,
        updated_at: new Date().toISOString()
      })
      .eq('id', commentId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Soft delete a comment
  async deleteLOComment(commentId: string) {
    const { data, error } = await supabase
      .from('lo_comments')
      .update({
        is_deleted: true,
        updated_at: new Date().toISOString()
      })
      .eq('id', commentId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // ============================================
  // HANDOFFS
  // ============================================

  // Create a handoff
  async createHandoff(params: {
    from_user_id: string;
    from_role: 'admin' | 'va';
    to_role: 'admin' | 'va';
    loan_officer_id?: string;
    prospect_id?: string;
    progress_id?: string;
    message?: string;
  }) {
    // Get from_user details for context
    const { data: fromUser } = await supabase
      .from('profiles')
      .select('first_name, last_name')
      .eq('id', params.from_user_id)
      .single();

    // Get subject name (LO or Prospect)
    let subjectName = '';
    let actionUrl = 'https://onboarding.luminlending.com/admin';

    if (params.loan_officer_id) {
      const { data: lo } = await supabase
        .from('profiles')
        .select('first_name, last_name')
        .eq('id', params.loan_officer_id)
        .single();
      if (lo) {
        subjectName = `${lo.first_name} ${lo.last_name}`;
        actionUrl = `https://onboarding.luminlending.com/admin/loan-officers/${params.loan_officer_id}`;
      }
    } else if (params.prospect_id) {
      const { data: prospect } = await supabase
        .from('prospects')
        .select('first_name, last_name')
        .eq('id', params.prospect_id)
        .single();
      if (prospect) {
        subjectName = `${prospect.first_name} ${prospect.last_name}`;
        actionUrl = 'https://onboarding.luminlending.com/admin/prospects';
      }
    }

    const { data, error } = await supabase
      .from('handoffs')
      .insert({
        ...params,
        is_acknowledged: false,
        created_at: new Date().toISOString()
      })
      .select()
      .single();

    if (error) throw error;

    // Trigger handoff notification via process-notification (uses notification rules)
    const fromUserName = fromUser ? `${fromUser.first_name} ${fromUser.last_name}` : 'A team member';
    const fromRoleDisplay = params.from_role === 'admin' ? 'Admin' : 'VA';
    const toRoleDisplay = params.to_role === 'admin' ? 'Admin Team' : 'VA Team';
    const handoffContext = `${fromUserName} (${fromRoleDisplay}) has handed off to the ${toRoleDisplay}.`;
    const messageSection = params.message ? `
      <div style="border-top: 1px solid #e0e0e0; padding-top: 15px; margin-top: 15px;">
        <p style="margin: 0; font-style: italic; color: #666;">"${params.message}"</p>
      </div>
    ` : '';

    try {
      await supabase.functions.invoke('process-notification', {
        body: {
          trigger_type: 'manual_handoff',
          trigger_value: params.to_role, // 'admin' or 'va'
          context: {
            handoff_id: data.id,
            from_user_id: params.from_user_id,
            from_name: fromUserName,
            from_role: fromRoleDisplay,
            to_role: toRoleDisplay,
            loan_officer_id: params.loan_officer_id || null,
            prospect_id: params.prospect_id || null,
            subject_name: subjectName,
            handoff_context: handoffContext,
            message: params.message || '',
            message_section: messageSection,
            action_url: actionUrl,
            handoff_icon: '📋',
          }
        }
      });
    } catch (notifyError) {
      console.error('Failed to trigger handoff notification:', notifyError);
      // Don't throw - handoff was created successfully
    }

    return data;
  },

  // Acknowledge a handoff
  async acknowledgeHandoff(handoffId: string, acknowledgedBy: string) {
    const { data, error } = await supabase
      .from('handoffs')
      .update({
        is_acknowledged: true,
        acknowledged_by: acknowledgedBy,
        acknowledged_at: new Date().toISOString()
      })
      .eq('id', handoffId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Get handoffs for a loan officer or prospect
  async getHandoffs(params: {
    loan_officer_id?: string;
    prospect_id?: string;
    to_role?: 'admin' | 'va';
    pending_only?: boolean;
  }) {
    let query = supabase
      .from('handoffs')
      .select(`
        *,
        from_user:profiles!handoffs_from_user_id_fkey(first_name, last_name),
        loan_officer:profiles!handoffs_loan_officer_id_fkey(id, first_name, last_name)
      `)
      .order('created_at', { ascending: false });

    if (params.loan_officer_id) {
      query = query.eq('loan_officer_id', params.loan_officer_id);
    }
    if (params.prospect_id) {
      query = query.eq('prospect_id', params.prospect_id);
    }
    if (params.to_role) {
      query = query.eq('to_role', params.to_role);
    }
    if (params.pending_only) {
      query = query.eq('is_acknowledged', false);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data;
  },

  // ============================================
  // NOTIFICATION RULES
  // ============================================

  // Get all notification rules
  async getNotificationRules(activeOnly = false) {
    let query = supabase
      .from('notification_rules')
      .select('*')
      .order('name', { ascending: true });

    if (activeOnly) {
      query = query.eq('is_active', true);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data;
  },

  // Create a notification rule
  async createNotificationRule(params: {
    name: string;
    description?: string;
    trigger_type: string;
    trigger_step_id?: string;
    trigger_task_pattern?: string;
    trigger_prospect_status?: string;
    trigger_timeline_field?: string;
    trigger_handoff_to_role?: 'admin' | 'va';
    notify_role: 'admin' | 'va' | 'both';
    send_email?: boolean;
    notify_emails?: string[];
    cc_emails?: string[];
    bcc_emails?: string[];
    created_by?: string;
  }) {
    const { data, error } = await supabase
      .from('notification_rules')
      .insert({
        ...params,
        is_active: true,
        send_email: params.send_email !== false,
        notify_emails: params.notify_emails || [],
        cc_emails: params.cc_emails || [],
        bcc_emails: params.bcc_emails || [],
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Update a notification rule
  async updateNotificationRule(ruleId: string, updates: {
    name?: string;
    description?: string | null;
    trigger_type?: string;
    trigger_step_id?: string | null;
    trigger_task_pattern?: string | null;
    trigger_prospect_status?: string | null;
    trigger_timeline_field?: string | null;
    trigger_handoff_to_role?: 'admin' | 'va' | null;
    notify_role?: 'admin' | 'va' | 'both';
    send_email?: boolean;
    notify_emails?: string[];
    cc_emails?: string[];
    bcc_emails?: string[];
    is_active?: boolean;
  }) {
    const { data, error } = await supabase
      .from('notification_rules')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', ruleId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Delete a notification rule
  async deleteNotificationRule(ruleId: string) {
    const { error } = await supabase
      .from('notification_rules')
      .delete()
      .eq('id', ruleId);

    if (error) throw error;
    return { success: true };
  },

  // Toggle notification rule active status
  async toggleNotificationRule(ruleId: string, isActive: boolean) {
    const { data, error } = await supabase
      .from('notification_rules')
      .update({
        is_active: isActive,
        updated_at: new Date().toISOString()
      })
      .eq('id', ruleId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // ============ Email Templates ============

  // Get all email templates
  async getEmailTemplates() {
    const { data, error } = await supabase
      .from('email_templates')
      .select('*')
      .order('name', { ascending: true });

    if (error) throw error;
    return data;
  },

  // Get a single email template by slug
  async getEmailTemplate(slug: string) {
    const { data, error } = await supabase
      .from('email_templates')
      .select('*')
      .eq('slug', slug)
      .single();

    if (error) throw error;
    return data;
  },

  // Update an email template
  async updateEmailTemplate(id: string, updates: {
    name?: string;
    description?: string;
    subject?: string;
    html_body?: string;
    is_active?: boolean;
  }) {
    const { data, error } = await supabase
      .from('email_templates')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // ==================== CEO Timeline ====================

  // Helper: UTC-safe date string formatting (avoids timezone shift on DATE columns)
  // NOTE: month is 1-based (1=Jan, 12=Dec) to match human-readable input
  formatDateUTC(year: number, month: number, day: number): string {
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  },

  // Get all timeline entries with prospect/profile/onboarding data
  // NOTE: activeOnly filtering is done client-side because PostgREST .or() cannot mix base + related table fields
  // NOTE: profiles!loan_officer_id specifies which FK to use (avoids ambiguity with updated_by FK)
  // IMPORTANT: filters.month must be 1-based (1=Jan, 12=Dec) - matches typical UI dropdown values
  async getTimelineEntries(filters?: { month?: number; year?: number }) {
    let query = supabase
      .from('onboarding_timeline')
      .select(`
        *,
        prospect:prospects!inner(
          id, first_name, last_name, title, phone, email, nmls, status,
          company_email, expected_start_date,
          onboarding_data:prospect_onboarding_data(ca_dre, nmls, cell_phone, form_completed_at)
        ),
        profile:profiles!loan_officer_id(
          id, first_name, last_name, phone, cell_number, email,
          nmls_number, dre_number
        )
      `)
      .order('sponsor_date', { ascending: true, nullsFirst: false });

    // Apply month/year filter on sponsor_date (UTC-safe)
    // NOTE: filters.month is 1-based; Date.UTC expects 0-based months
    // Passing 1-based month + day 0 gives last day of intended month
    // e.g., month=3 (March) → Date.UTC(year, 3, 0) = last day of March
    if (filters?.month && filters?.year) {
      const startDate = this.formatDateUTC(filters.year, filters.month, 1);
      const lastDay = new Date(Date.UTC(filters.year, filters.month, 0)).getUTCDate();
      const endDate = this.formatDateUTC(filters.year, filters.month, lastDay);
      query = query.gte('sponsor_date', startDate).lte('sponsor_date', endDate);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data as TimelineEntry[];
  },

  // Update timeline entry - only allows timeline-specific fields (not nested prospect/profile)
  async updateTimelineEntry(
    id: string,
    updates: Partial<Pick<TimelineEntry, 'unsponsor_date' | 'sponsor_date' | 'training_date' | 'leads_live_date' | 'notes'>>,
    updatedBy: string
  ) {
    // Extract only allowed fields to prevent sending nested objects
    const { unsponsor_date, sponsor_date, training_date, leads_live_date, notes } = updates;
    const safeUpdates: Record<string, unknown> = {
      updated_by: updatedBy,
      updated_at: new Date().toISOString(),
    };
    if (unsponsor_date !== undefined) safeUpdates.unsponsor_date = unsponsor_date;
    if (sponsor_date !== undefined) safeUpdates.sponsor_date = sponsor_date;
    if (training_date !== undefined) safeUpdates.training_date = training_date;
    if (leads_live_date !== undefined) safeUpdates.leads_live_date = leads_live_date;
    if (notes !== undefined) safeUpdates.notes = notes;

    const { data, error } = await supabase
      .from('onboarding_timeline')
      .update(safeUpdates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }
};
